import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Container,
  Avatar,
  Stack,
  Paper,
  SelectChangeEvent,
  CircularProgress,
} from '@mui/material';
import { toast } from 'react-toastify';
import ChargebeeService from '../../services/chargebee.service';
import { decodeIdToken, decodeMetadataToken } from '../../utils/token.utils';
import { styled, keyframes } from '@mui/material/styles';
import {
  X,
  Pause,
  Gift,
  CreditCard,
  XCircle,
  ChevronDown,
  Zap,
  TrendingUp,
  Phone,
  MessageCircle,
  DollarSign,
  Users,
  Clock,
  AlertCircle,
  Star,
  BarChart3,
  CheckCircle2,
  Database,
  Sparkles,
  ArrowRight,
  Crown,
} from 'lucide-react';

// Animations
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const zoomIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
`;

const bounceSlow = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
`;

// Styled Components
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '24px',
    maxWidth: '712px',
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto',
  },
  '& .MuiBackdrop-root': {
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    backdropFilter: 'blur(4px)',
  },
}));

const AnimatedBox = styled(Box)({
  animation: `${zoomIn} 200ms ease-out`,
});

const BounceBox = styled(Box)({
  animation: `${bounceSlow} 2s ease-in-out infinite`,
});

const HoverCard = styled(Card)(({ theme }) => ({
  cursor: 'pointer',
  transition: 'all 200ms',
  '&:hover': {
    transform: 'scale(1.02)',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.2)',
  },
}));

interface SubscriptionItemProp {
  itemPriceId: string;
  itemName: string;
  itemType: 'plan' | 'addon';
  quantity: number;
  unitPrice: number;
  amount: number;
}

/** Returns itemPriceIds of all addons to pass as mandatoryItemsToRemove when changing to subscription-pause */
const getAllAddonItemPriceIdsToRemove = (items: SubscriptionItemProp[]): string[] =>
  items
    .filter((item) => item.itemType === 'addon' && item.itemPriceId)
    .map((item) => item.itemPriceId);

interface CancelSubscriptionModalProps {
  open?: boolean;
  onClose?: () => void;
  navigate?: (path: string) => void;
  subscriptionId?: string;
  subscriptionType?: 'trial' | 'paid';
  subscriptionItems?: SubscriptionItemProp[];
  cancellationSnapshot?: {
    totalAmount?: number;
    planId?: string;
    subscriptionStatus?: string;
    startedAt?: number;
  };
}

const CancelSubscriptionModal = ({
  open = false,
  onClose,
  navigate,
  subscriptionId,
  subscriptionType = 'paid',
  subscriptionItems = [],
  cancellationSnapshot,
}: CancelSubscriptionModalProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [selectedReason, setSelectedReason] = useState('');
  const [feedback, setFeedback] = useState('');
  const [selectedPauseOption, setSelectedPauseOption] = useState('');
  const [cancellingSubscription, setCancellingSubscription] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isExtending, setIsExtending] = useState(false);
  const [isPausing, setIsPausing] = useState(false);
  const [isDowngrading, setIsDowngrading] = useState(false);

  // Sync internal state with props
  useEffect(() => {
    if (open) {
      setCancellingSubscription(subscriptionType);
      setStep(0.5);
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [open, subscriptionType]);

  const pausePlanPrice = 9.99;
  const isPausedPlan =
    cancellationSnapshot?.subscriptionStatus === 'paused' ||
    cancellationSnapshot?.planId?.toLowerCase().includes('subscription-pause');

  const cancellationReasons = [
    { value: 'performance', label: "Webinar/funnel didn't perform as expected" },
    { value: 'setup', label: "Haven't launched yet/stuck during setup" },
    { value: 'time', label: 'No time/bandwidth to manage webinars' },
    { value: 'technical', label: 'Technical issues during events' },
    { value: 'expensive', label: 'Too expensive' },
    { value: 'other', label: 'Other' },
  ];

  const interventionBanners = {
    performance: {
      icon: '📊',
      title: 'Free Funnel Review Call',
      description:
        'Let our experts analyze your funnel and show you proven strategies to improve performance. Free 30-minute session.',
      buttonText: 'Book Free Review Call',
      bgColor: '#EBF5FF',
      borderColor: '#BFDBFE',
      buttonGradient: 'linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)',
    },
    setup: {
      icon: '🚀',
      title: 'Free Setup Assistance',
      description:
        "Get personalized help from our team to get you up and running. We'll guide you through every step.",
      buttonText: 'Get Free Setup Help',
      bgColor: '#FAF5FF',
      borderColor: '#E9D5FF',
      buttonGradient: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
    },
    time: {
      icon: '⚡',
      title: 'Done-For-You Service',
      description:
        'Let us handle the heavy lifting. Our team can manage your webinars while you focus on what matters most.',
      buttonText: 'Learn About DFY Service',
      bgColor: '#EEF2FF',
      borderColor: '#C7D2FE',
      buttonGradient: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
    },
    technical: {
      icon: '🛠️',
      title: 'Priority Tech Support',
      description:
        "Let's fix this together. Schedule a call with our tech team to resolve any issues you're experiencing.",
      buttonText: 'Get Tech Support',
      bgColor: '#FEFCE8',
      borderColor: '#FEF08A',
      buttonGradient: 'linear-gradient(135deg, #EAB308 0%, #F97316 100%)',
    },
    expensive: {
      // icon: '💰',
      // title: 'Special Pricing for You',
      // description:
      //   'We understand cost is a concern. Continue with 30% off your current plan - no commitment required.',
      // buttonText: 'Claim 30% OFF & Continue',
      // bgColor: '#ECFDF5',
      // borderColor: '#A7F3D0',
      // buttonGradient: 'linear-gradient(135deg, #10B981 0%, #14B8A6 100%)',
      icon: '💬',
      title: 'Chat With Us',
      description:
        "We'd love to understand your situation better. Chat with our customer success team to explore options.",
      buttonText: 'Start Chat',
      bgColor: '#F8FAFC',
      borderColor: '#CBD5E1',
      buttonGradient: 'linear-gradient(135deg, #64748B 0%, #475569 100%)',
    },
    other: {
      icon: '💬',
      title: 'Chat With Us',
      description:
        "We'd love to understand your situation better. Chat with our customer success team to explore options.",
      buttonText: 'Start Chat',
      bgColor: '#F8FAFC',
      borderColor: '#CBD5E1',
      buttonGradient: 'linear-gradient(135deg, #64748B 0%, #475569 100%)',
    },
  };

  const handleAcceptOffer = async (offerType: string) => {
    let message = '';

    switch (offerType) {
      case 'pause':
        setStep(1.5);
        return;
      case 'pause-immediate':
        if (!subscriptionId) {
          toast.error('Subscription ID is required to pause subscription');
          return;
        }
        try {
          setIsPausing(true);
          const itemsToRemove = getAllAddonItemPriceIdsToRemove(subscriptionItems);
          await ChargebeeService.changePlan(
            subscriptionId,
            'subscription-pause',
            undefined,
            undefined,
            undefined,
            undefined,
            itemsToRemove.length > 0 ? itemsToRemove : undefined
          );
          message = 'Your subscription has been paused immediately. All data is preserved.';
          toast.success('Subscription paused successfully');
          setIsOpen(false);
          if (onClose) onClose();
        } catch (error: any) {
          console.error('Error pausing subscription:', error);
          toast.error(error.message || 'Failed to pause subscription. Please try again.');
          setIsPausing(false);
          return;
        } finally {
          setIsPausing(false);
        }
        break;
      case 'pause-end-billing':
        if (!subscriptionId) {
          toast.error('Subscription ID is required to pause subscription');
          return;
        }
        try {
          setIsPausing(true);
          const itemsToRemove = getAllAddonItemPriceIdsToRemove(subscriptionItems);
          await ChargebeeService.changePlan(
            subscriptionId,
            'subscription-pause',
            undefined,
            undefined,
            undefined,
            'end_of_term',
            itemsToRemove.length > 0 ? itemsToRemove : undefined
          );
          message = 'Your subscription will pause at the end of your billing term. All data is preserved.';
          toast.success('Subscription will pause at end of billing term');
          setIsOpen(false);
          if (onClose) onClose();
        } catch (error: any) {
          console.error('Error pausing subscription:', error);
          toast.error(error.message || 'Failed to pause subscription. Please try again.');
          setIsPausing(false);
          return;
        } finally {
          setIsPausing(false);
        }
        break;
      case 'trial-extension':
        message = 'Your trial has been extended by 7 days. Enjoy exploring!';
        setIsOpen(false);
        if (onClose) onClose();
        break;
      case '30-day-extension':
        if (!subscriptionId) {
          toast.error('Subscription ID is required to extend billing date');
          return;
        }
        try {
          setIsExtending(true);
          await ChargebeeService.extendBillingDate(subscriptionId, 30);
          message = "Great! We've added 30 free days to your subscription.";
          toast.success('Billing date extended successfully by 30 days');
          setIsOpen(false);
          if (onClose) onClose();
        } catch (error: any) {
          console.error('Error extending billing date:', error);
          toast.error(error.message || 'Failed to extend billing date. Please try again.');
          setIsExtending(false);
          return;
        } finally {
          setIsExtending(false);
        }
        break;
      case 'free-plan':
        if (!subscriptionId) {
          toast.error('Subscription ID is required to switch to Free Plan');
          return;
        }
        try {
          setIsDowngrading(true);
          await ChargebeeService.changePlan(subscriptionId, 'free-trial-plan');
          message =
            cancellingSubscription === 'paid'
              ? "You've been downgraded to the Free Plan."
              : 'Welcome to the Free Plan!';
          toast.success('Successfully switched to Free Plan');
          setIsOpen(false);
          if (onClose) onClose();
        } catch (error: any) {
          console.error('Error switching to Free Plan:', error);
          toast.error(error.message || 'Failed to switch to Free Plan. Please try again.');
          setIsDowngrading(false);
          return;
        } finally {
          setIsDowngrading(false);
        }
        break;
      case 'final-cancel':
        // Validate required fields
        if (!selectedReason) {
          toast.error('Please select a cancellation reason');
          return;
        }

        // Submit cancellation reason before closing
        try {
          const idToken = decodeIdToken();
          const metadata = decodeMetadataToken();
          
          if (!idToken || !metadata) {
            console.error('Failed to decode tokens');
            toast.error('Failed to process cancellation. Please try again.');
            return;
          }

          const email = idToken.email;
          const cognito_user_id = idToken.sub;
          const customer_id = metadata?.companies?.[0]?.id;

          if (!email || !cognito_user_id || !customer_id || !subscriptionId) {
            console.error('Missing required cancellation data');
            toast.error('Missing required information. Please try again.');
            return;
          }

          // Get the cancellation reason label from the selected value
          const reasonLabel = cancellationReasons.find(r => r.value === selectedReason)?.label || selectedReason;
          
          // Submit cancellation reason (don't await to avoid blocking UI)
          ChargebeeService.submitCancellationReason({
            email,
            customer_id,
            cognito_user_id,
            subscription_id: subscriptionId,
            cancellation_reason: reasonLabel,
            ...(typeof cancellationSnapshot?.totalAmount === 'number' && { current_amount: cancellationSnapshot.totalAmount }),
            ...(cancellationSnapshot?.planId && { plan_id: cancellationSnapshot.planId }),
            ...(cancellationSnapshot?.subscriptionStatus && {
              subscription_status: cancellationSnapshot.subscriptionStatus,
            }),
            ...(typeof cancellationSnapshot?.startedAt === 'number' && { started_at: cancellationSnapshot.startedAt }),
            ...(feedback && { feedback })
          }).catch((error) => {
            console.error('Failed to submit cancellation reason:', error);
            // Don't show error to user as cancellation should proceed
          });
        } catch (error) {
          console.error('Error submitting cancellation reason:', error);
          // Don't block cancellation if API call fails
        }

        try {
          await ChargebeeService.cancelSubscription(subscriptionId as string);
        } catch (error: any) {
          console.error('Error cancelling subscription:', error);
          toast.error(error.message || 'Failed to cancel subscription. Please try again.');
          return;
        }
        message = 'Your subscription has been cancelled successfully.';
        setIsOpen(false); // Close the main dialog before showing success
        if (onClose) onClose();
        break;
      case 'upgrade-promo':
        message = 'Success! Your 30% discount has been applied. Upgrade now to start saving!';
        setIsOpen(false);
        if (onClose) onClose();
        break;
      case 'upgrade-live-automated':
        message = "Success! You've upgraded to Live + Automated Plan with 30% off for 3 months!";
        setIsOpen(false);
        if (onClose) onClose();
        break;
      case 'upgrade-starter':
        message = "Success! You've upgraded to Starter Plan with 20% off for 3 months!";
        setIsOpen(false);
        if (onClose) onClose();
        break;
      case 'free-setup':
      case 'request-feature':
      case 'tech-issue':
      case 'promo-code':
      case 'demo-call':
        setIsOpen(false);
        if (onClose) onClose();
        // Open Intercom chat to connect with support
        if ((window as any).Intercom) {
          (window as any).Intercom('show');
        }
        message = 'Please chat with our support team — they are ready to help!';
        break;
      default:
        if (offerType.startsWith('intervention-')) {
          setIsOpen(false);
          if (onClose) onClose();
          // Open Intercom chat to connect with support
          if ((window as any).Intercom) {
            (window as any).Intercom('show');
          }
          message = 'Please chat with our support team — they are ready to help!';
        } else {
          message = 'Thank you for your feedback. Your trial/subscription has been cancelled.';
          setIsOpen(false);
          if (onClose) onClose();
        }
    }

    setSuccessMessage(message);
    setShowSuccess(true);
    if (offerType === 'pause-immediate' || offerType === 'pause-end-billing') {
      localStorage.setItem('isPauseAction', 'true');
    } else {
      localStorage.setItem('isPauseAction', 'false');
    }
  };

  const closeModal = () => {
    setIsOpen(false);
    if (onClose) {
      onClose();
    }
    setTimeout(() => {
      setStep(1);
      setSelectedReason('');
      setFeedback('');
      setShowSuccess(false);
      setSuccessMessage('');
      setSelectedPauseOption('');
      setCancellingSubscription('');
    }, 300);
  };

  const closeSuccessModal = () => {
    setShowSuccess(false);
    if (onClose) {
      onClose();
    }
    setTimeout(() => {
      setSuccessMessage('');
      setSelectedPauseOption('');
      setCancellingSubscription('');
      setStep(1);
      setSelectedReason('');
      setFeedback('');
    }, 300);
  };

  return (
    <>
      {/* Modal/Dialog */}
      <StyledDialog open={isOpen && !showSuccess} onClose={closeModal}>
        <Box>
          <AnimatedBox>
            {/* Step 0.5 - Universal Wait Don't Go */}
            {step === 0.5 && (
              <Box>
                {/* Header */}
                <Box
                  sx={{
                    background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
                    px: 3,
                    py: 2.5,
                    position: 'relative',
                  }}
                >
                  <IconButton
                    onClick={closeModal}
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      color: 'white',
                      '&:hover': { color: '#E5E7EB' },
                    }}
                  >
                    <X size={20} />
                  </IconButton>
                  <Box sx={{ display: 'flex', alignItems: 'start', gap: 1.5 }}>
                    <Avatar
                      sx={{
                        width: 48,
                        height: 48,
                        bgcolor: 'rgba(255,255,255,0.2)',
                        borderRadius: '12px',
                      }}
                    >
                      <Crown size={28} color="white" />
                    </Avatar>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', mb: 0 }}>
                        Wait! Don't Go Yet
                      </Typography>
                      {/* <Typography variant="body2" sx={{ color: '#fff' }}>
                        {cancellingSubscription === 'trial'
                          ? 'Professional Plan - 14 days left in your trial'
                          : 'Professional Plan - $29/month'}
                      </Typography> */}
                    </Box>
                  </Box>
                </Box>

                {/* Content */}
                <Box sx={{ p: 3 }}>
                  <Typography sx={{ fontWeight: 600, color: '#0F172A', mb: 1 }}>
                    Cancelling your subscription? Here's what you'll lose:
                  </Typography>

                  <Grid container spacing={1.5} sx={{ mb: 3 }}>
                    {[
                      {
                        icon: <Database size={20} color="#4F46E5" />,
                        title: 'Your custom webinar settings',
                        desc: 'All personalized configurations will be deleted',
                      },
                      {
                        icon: <Users size={20} color="#4F46E5" />,
                        title: 'All recorded webinar sessions',
                        desc: 'Linked to your account - permanently removed',
                      },
                      {
                        icon: <BarChart3 size={20} color="#4F46E5" />,
                        title: 'Past webinar registrants data',
                        desc: 'Analytics and insights will be lost forever',
                      },
                      {
                        icon: <Zap size={20} color="#4F46E5" />,
                        title: 'CRM integrations',
                        desc: 'All integrations from your subscription plan',
                      },
                    ].map((item, i) => (
                      <Grid item xs={6} key={i}>
                        <Paper
                          sx={{
                            p: 2,
                            borderRadius: '12px',
                            bgcolor: '#F8FAFC',
                            border: '1px solid #E2E8F0',
                            height: '100%',
                            boxShadow: 'none',
                          }}
                        >
                          <Avatar
                            sx={{
                              width: 40,
                              height: 40,
                              bgcolor: '#EEF2FF',
                              borderRadius: '10px',
                              mb: 1,
                            }}
                          >
                            {item.icon}
                          </Avatar>
                          <Typography sx={{ fontWeight: 600, color: '#0F172A', fontSize: '13px', mb: 0.5 }}>
                            {item.title}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#64748B', display: 'block', fontSize: '11px' }}>
                            {item.desc}
                          </Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>

                  {/* Upgrade Offers for Trial */}
                  {cancellingSubscription === 'trial' && (
                    <HoverCard
                      onClick={() => setStep(1.7)}
                      sx={{
                        mb: 2,
                        bgcolor: '#ECFDF5',
                        borderRadius: '16px',
                        p: 2,
                        border: '1px solid #A7F3D0',
                        boxShadow: 'none',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
                        <Avatar sx={{ bgcolor: 'white', width: 40, height: 40, borderRadius: '12px' }}>
                          <TrendingUp size={24} color="#10B981" />
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography sx={{ fontWeight: 600, color: '#0F172A' }}>
                              View Upgrade Offers
                            </Typography>
                            <Chip
                              label="Special Deal"
                              size="small"
                              sx={{ bgcolor: '#D1FAE5', color: '#065F46', height: 20, fontSize: '11px' }}
                            />
                          </Box>
                          <Typography variant="body2" sx={{ color: '#64748B' }}>
                            Get exclusive discounts on paid plans. Limited time offer!
                          </Typography>
                        </Box>
                        <ArrowRight size={20} color="#94A3B8" />
                      </Box>
                    </HoverCard>
                  )}

                  {/* Pause for Paid */}
                  {cancellingSubscription === 'paid' && (
                    <Paper
                      sx={{
                        mb: 2,
                        bgcolor: '#FAF5FF',
                        borderRadius: '16px',
                        p: 2.5,
                        border: '1px solid #E9D5FF',
                        transition: 'all 200ms',
                        '&:hover': {
                          bgcolor: '#F3E8FF',
                          borderColor: '#A855F7',
                          boxShadow: '0 4px 6px -1px rgba(168, 85, 247, 0.2)',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
                        <Box sx={{ display: 'flex', width: 40, height: 40 }}>
                          <Pause size={24} color="#7C3AED" />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'start', justifyContent: 'space-between', mb: 1 }}>
                            <Typography sx={{ fontWeight: 600, color: '#0F172A' }}>
                              Pause Subscription
                            </Typography>
                            <Box
                              sx={{
                                background: 'linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)',
                                px: 2,
                                py: 1,
                                borderRadius: '8px',
                                boxShadow: '0 2px 8px rgba(124, 58, 237, 0.3)',
                                display: 'flex',
                              }}
                            >
                              <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '18px' }}>
                                ${pausePlanPrice}
                              </Typography>
                              <Typography sx={{ color: '#DDD6FE', fontSize: '14px', fontWeight: 500, position: 'relative', top: '5px',}}>
                                /month
                              </Typography>
                            </Box>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'start', gap: 1, mb: 2 }}>
                            <Database size={16} color="#64748B" style={{ marginTop: 2, flexShrink: 0 }} />
                            <Typography variant="body2" sx={{ color: '#64748B' }}>
                              All your data stays safely stored. Resume anytime and pick up exactly where you left
                              off.
                            </Typography>
                          </Box>
                          <Button
                            fullWidth
                            onClick={() => !isPausedPlan && handleAcceptOffer('pause')}
                            disabled={isPausedPlan}
                            sx={{
                              bgcolor: '#4F46E5',
                              color: 'white !important',
                              fontWeight: 500,
                              textTransform: 'none',
                              borderRadius: '8px',
                              py: 1.5,
                              fontSize: '14px',
                              '&:hover': { 
                                bgcolor: '#4338CA',
                                boxShadow: '0 4px 12px rgba(79, 70, 229, 0.4)',
                              },
                              '&:disabled': {
                                background: '#94A3B8',
                                color: 'white !important',
                                opacity: 0.7,
                              },
                            }}
                          >
                            Pause for Now
                          </Button>
                        </Box>
                      </Box>
                    </Paper>
                  )}

                  {/* Bottom buttons grid */}
                  <Grid container spacing={1.5}>
                    <Grid item xs={6}>
                      <Button
                        fullWidth
                        onClick={() => {
                          if (cancellingSubscription === 'trial') {
                            setStep(1.6);
                          } else {
                            setStep(2);
                          }
                        }}
                        variant="outlined"
                        sx={{
                          borderColor: '#CBD5E1',
                          borderWidth: '1px',
                          color: '#334155',
                          fontWeight: 600,
                          textTransform: 'none',
                          borderRadius: '12px',
                          py: 1.5,
                          '&:hover': { 
                            bgcolor: '#F8FAFC',
                            borderWidth: '1px',
                            borderColor: '#CBD5E1',
                          },
                        }}
                      >
                        {cancellingSubscription === 'trial' ? 'Cancel Trial' : 'Cancel Subscription'}
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <Button
                        fullWidth
                        onClick={() =>
                          handleAcceptOffer(cancellingSubscription === 'trial' ? 'free-plan' : '30-day-extension')
                        }
                        disabled={isExtending || isDowngrading || isPausedPlan}
                        sx={{
                          background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
                          color: 'white !important',
                          fontWeight: 600,
                          textTransform: 'none',
                          borderRadius: '12px',
                          py: 1.5,
                          boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)',
                          '&:hover': { 
                            opacity: 0.9,
                            boxShadow: '0 6px 16px rgba(79, 70, 229, 0.4)',
                          },
                          '&:disabled': {
                            background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
                            opacity: 0.6,
                          },
                        }}
                      >
                        {isExtending ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={16} sx={{ color: 'white' }} />
                            <span>Extending...</span>
                          </Box>
                        ) : isDowngrading ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <CircularProgress size={16} sx={{ color: 'white' }} />
                            <span>Switching...</span>
                          </Box>
                        ) : (
                          cancellingSubscription === 'trial' ? 'Switch to Free Plan' : 'Extend for 30 days'
                        )}
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            )}

            {/* Step 1: Trial - Before You Go */}
            {step === 1 && cancellingSubscription === 'trial' && (
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#0F172A', mb: 0.5 }}>
                      Before you go...
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748B' }}>
                      Explore these options before ending your trial
                    </Typography>
                  </Box>
                  <IconButton onClick={closeModal} sx={{ color: '#94A3B8' }}>
                    <X size={20} />
                  </IconButton>
                </Box>

                <Stack spacing={1.5}>
                  <HoverCard
                    onClick={() => handleAcceptOffer('free-plan')}
                    sx={{
                      bgcolor: '#EFF6FF',
                      borderRadius: '16px',
                      p: 2.5,
                      border: '1px solid #BFDBFE',
                      transition: 'all 200ms',
                      '&:hover': {
                        bgcolor: '#DBEAFE',
                        borderColor: '#3B82F6',
                        transform: 'scale(1.02)',
                        boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.2)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
                      <Avatar sx={{ bgcolor: 'white', width: 40, height: 40, borderRadius: '12px' }}>
                        <CreditCard size={24} color="#3B82F6" />
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontWeight: 600, color: '#0F172A', mb: 0.5 }}>
                          Switch to Free Plan
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#64748B' }}>
                          Continue with basic features at no cost. Upgrade anytime when ready.
                        </Typography>
                      </Box>
                      <ArrowRight size={20} color="#94A3B8" style={{ marginTop: 4 }} />
                    </Box>
                  </HoverCard>

                  <HoverCard
                    onClick={() => setStep(1.7)}
                    sx={{
                      bgcolor: '#ECFDF5',
                      borderRadius: '16px',
                      p: 2.5,
                      border: '1px solid #A7F3D0',
                      transition: 'all 200ms',
                      '&:hover': {
                        bgcolor: '#D1FAE5',
                        borderColor: '#10B981',
                        transform: 'scale(1.02)',
                        boxShadow: '0 4px 6px -1px rgba(16, 185, 129, 0.2)',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
                      <Avatar sx={{ bgcolor: 'white', width: 40, height: 40, borderRadius: '12px' }}>
                        <TrendingUp size={24} color="#10B981" />
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography sx={{ fontWeight: 600, color: '#0F172A' }}>View Upgrade Offers</Typography>
                          <Chip
                            label="Special Deal"
                            size="small"
                            sx={{ bgcolor: '#D1FAE5', color: '#065F46', height: 20, fontSize: '11px' }}
                          />
                        </Box>
                        <Typography variant="body2" sx={{ color: '#64748B' }}>
                          Get exclusive discounts on paid plans. Limited time offer!
                        </Typography>
                      </Box>
                      <ArrowRight size={20} color="#94A3B8" style={{ marginTop: 4 }} />
                    </Box>
                  </HoverCard>

                  <Button
                    fullWidth
                    onClick={() => setStep(1.6)}
                    variant="outlined"
                    endIcon={<ArrowRight size={16} />}
                    sx={{
                      borderColor: '#E2E8F0',
                      color: '#334155',
                      fontWeight: 500,
                      textTransform: 'none',
                      borderRadius: '8px',
                      py: 1.5,
                      '&:hover': { 
                        bgcolor: '#F8FAFC',
                        borderColor: '#CBD5E1',
                      },
                    }}
                  >
                    View Cancellation Options
                  </Button>
                </Stack>
              </Box>
            )}

            {/* Step 1.6: Trial - How can we help */}
            {step === 1.6 && cancellingSubscription === 'trial' && (
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#0F172A', mb: 0.5 }}>
                      How can we help?
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748B' }}>
                      Tell us what's stopping you and we'll try to help
                    </Typography>
                  </Box>
                  <IconButton onClick={closeModal} sx={{ color: '#94A3B8' }}>
                    <X size={20} />
                  </IconButton>
                </Box>

                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                  {[
                    { icon: <Zap size={20} color="#7C3AED" />, title: 'Request Free Setup', chip: 'Free', bg: '#FAF5FF', border: '#E9D5FF', action: 'free-setup' },
                    { icon: <Star size={20} color="#3B82F6" />, title: 'Request a Feature', chip: null, bg: '#EFF6FF', border: '#BFDBFE', action: 'request-feature' },
                    { icon: <AlertCircle size={20} color="#EAB308" />, title: 'Report Technical Issue', chip: null, bg: '#FEFCE8', border: '#FEF08A', action: 'tech-issue' },
                    { icon: <Gift size={20} color="#10B981" />, title: 'Get Promo Code', chip: '30% OFF', bg: '#ECFDF5', border: '#A7F3D0', action: 'promo-code' },
                    { icon: <Phone size={20} color="#6366F1" />, title: 'Schedule a Demo Call', chip: null, bg: '#EEF2FF', border: '#C7D2FE', action: 'demo-call' },
                    { icon: <XCircle size={20} color="#EF4444" />, title: 'End Trial', chip: null, bg: '#FEF2F2', border: '#FECACA', action: 'goto-step-3' },
                  ].map((item, i) => (
                    <Grid item xs={6} key={i}>
                      <HoverCard
                        onClick={() => item.action === 'goto-step-3' ? setStep(3) : handleAcceptOffer(item.action)}
                        sx={{
                          bgcolor: item.bg,
                          borderRadius: '16px',
                          p: 2,
                          border: `2px solid ${item.border}`,
                          height: 100,
                          display: 'flex',
                          flexDirection: 'column',
                          transition: 'all 200ms',
                          boxShadow: 'none',
                          '&:hover': {
                            transform: 'scale(1.02)',
                            boxShadow: `0 4px 6px -1px ${item.border}80`,
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'start', gap: 1.5, mb: 1 }}>
                          <Avatar sx={{ bgcolor: 'white', width: 32, height: 32, borderRadius: '10px' }}>
                            {item.icon}
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                              <Typography sx={{ fontWeight: 600, color: '#0F172A', fontSize: '13px' }}>
                                {item.title}
                              </Typography>
                              {item.chip && (
                                <Chip
                                  label={item.chip}
                                  size="small"
                                  sx={{
                                    height: 16,
                                    fontSize: '10px',
                                    bgcolor: item.action === 'promo-code' ? '#D1FAE5' : '#DDD6FE',
                                    color: item.action === 'promo-code' ? '#065F46' : '#5B21B6',
                                  }}
                                />
                              )}
                            </Box>
                            <Typography variant="caption" sx={{ color: '#64748B', display: 'block', lineHeight: 1.3 }}>
                              {item.action === 'free-setup' && 'Our team will help you get started with a personalized setup session.'}
                              {item.action === 'request-feature' && "Missing something? Let us know and we'll consider adding it."}
                              {item.action === 'tech-issue' && 'Having problems? Our support team will help you resolve any issues.'}
                              {item.action === 'promo-code' && 'Claim a discount code for upgrading to a paid plan.'}
                              {item.action === 'demo-call' && 'Book a 1-on-1 call to see how the platform can work for you.'}
                              {item.action === 'goto-step-3' && 'End your trial period and lose access to all features.'}
                            </Typography>
                          </Box>
                          <ArrowRight size={16} color="#94A3B8" style={{ flexShrink: 0 }} />
                        </Box>
                      </HoverCard>
                    </Grid>
                  ))}
                </Grid>

                <Button
                  fullWidth
                  onClick={() => setStep(0.5)}
                  variant="outlined"
                  sx={{
                    borderColor: '#E2E8F0',
                    color: '#334155',
                    fontWeight: 500,
                    textTransform: 'none',
                    borderRadius: '8px',
                    py: 1.5,
                    '&:hover': { bgcolor: '#F8FAFC' },
                  }}
                >
                  Go Back
                </Button>
              </Box>
            )}

            {/* Step 1.7: Trial - Exclusive Upgrade Offers */}
            {step === 1.7 && cancellingSubscription === 'trial' && (
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Sparkles size={24} color="#4F46E5" />
                      <Typography variant="h5" sx={{ fontWeight: 600, color: '#0F172A' }}>
                        Exclusive Upgrade Offers
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: '#64748B' }}>
                      Special pricing just for you - limited time only
                    </Typography>
                  </Box>
                  <IconButton onClick={closeModal} sx={{ color: '#94A3B8' }}>
                    <X size={20} />
                  </IconButton>
                </Box>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {/* Live + Automated Plan */}
                  <Grid item xs={6}>
                    <Paper
                      sx={{
                        bgcolor: '#EEF2FF',
                        borderRadius: '16px',
                        p: 2.5,
                        border: '1px solid #C7D2FE',
                        height: '100%',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#0F172A' }}>
                          Live + Automated Plan
                        </Typography>
                        <Chip
                          label="30% OFF"
                          size="small"
                          sx={{ bgcolor: '#D1FAE5', color: '#065F46', fontWeight: 600, fontSize: '11px' }}
                        />
                      </Box>
                      <Box sx={{ mb: 0.5 }}>
                        <Typography
                          component="span"
                          sx={{ fontSize: '30px', fontWeight: 700, color: '#4F46E5' }}
                        >
                          $20.30
                        </Typography>
                        <Typography
                          component="span"
                          sx={{
                            fontSize: '16px',
                            color: '#64748B',
                            textDecoration: 'line-through',
                            ml: 1,
                          }}
                        >
                          $29
                        </Typography>
                        <Typography component="span" sx={{ fontSize: '14px', color: '#64748B', ml: 0.5 }}>
                          /month
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mb: 2 }}>
                        First 3 months, then $29/month
                      </Typography>

                      <Stack spacing={1} sx={{ mb: 2.5 }}>
                        {[
                          'Unlimited projects & webinars',
                          'Advanced analytics dashboard',
                          '24/7 priority support',
                          'Custom integrations & API access',
                          'Team collaboration tools',
                          'Advanced security features',
                        ].map((feature, i) => (
                          <Box key={i} sx={{ display: 'flex', alignItems: 'start', gap: 1 }}>
                            <CheckCircle2
                              size={16}
                              color="#10B981"
                              style={{ marginTop: 2, flexShrink: 0 }}
                            />
                            <Typography variant="body2" sx={{ color: '#334155', fontSize: '14px' }}>
                              {feature}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>

                      <Button
                        fullWidth
                        onClick={() => handleAcceptOffer('upgrade-live-automated')}
                        sx={{
                          background: 'linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%)',
                          color: 'white !important',
                          fontWeight: 600,
                          textTransform: 'none',
                          borderRadius: '8px',
                          py: 1.5,
                          fontSize: '14px',
                          boxShadow: '0 4px 12px rgba(79, 70, 229, 0.3)',
                          '&:hover': { opacity: 0.9 },
                        }}
                      >
                        Upgrade Now - Save $8.70/mo
                      </Button>
                    </Paper>
                  </Grid>

                  {/* Starter Plan */}
                  <Grid item xs={6}>
                    <Paper
                      sx={{
                        bgcolor: '#F8FAFC',
                        borderRadius: '16px',
                        p: 2.5,
                        border: '1px solid #E2E8F0',
                        height: '100%',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#0F172A' }}>
                          Starter Plan
                        </Typography>
                        <Chip
                          label="20% OFF"
                          size="small"
                          sx={{ bgcolor: '#DBEAFE', color: '#1E40AF', fontWeight: 600, fontSize: '11px' }}
                        />
                      </Box>
                      <Box sx={{ mb: 0.5 }}>
                        <Typography
                          component="span"
                          sx={{ fontSize: '30px', fontWeight: 700, color: '#0F172A' }}
                        >
                          $12
                        </Typography>
                        <Typography
                          component="span"
                          sx={{
                            fontSize: '16px',
                            color: '#64748B',
                            textDecoration: 'line-through',
                            ml: 1,
                          }}
                        >
                          $15
                        </Typography>
                        <Typography component="span" sx={{ fontSize: '14px', color: '#64748B', ml: 0.5 }}>
                          /month
                        </Typography>
                      </Box>
                      <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mb: 2 }}>
                        First 3 months, then $15/month
                      </Typography>

                      <Stack spacing={1} sx={{ mb: 2.5 }}>
                        {['Up to 10 projects', 'Basic analytics', 'Email support', 'Standard integrations'].map(
                          (feature, i) => (
                            <Box key={i} sx={{ display: 'flex', alignItems: 'start', gap: 1 }}>
                              <CheckCircle2
                                size={16}
                                color="#10B981"
                                style={{ marginTop: 2, flexShrink: 0 }}
                              />
                              <Typography variant="body2" sx={{ color: '#334155', fontSize: '14px' }}>
                                {feature}
                              </Typography>
                            </Box>
                          )
                        )}
                      </Stack>

                      <Button
                        fullWidth
                        onClick={() => handleAcceptOffer('upgrade-starter')}
                        sx={{
                          bgcolor: '#475569',
                          color: 'white !important',
                          fontWeight: 600,
                          textTransform: 'none',
                          borderRadius: '8px',
                          py: 1.5,
                          fontSize: '14px',
                          '&:hover': { bgcolor: '#334155' },
                        }}
                      >
                        Choose Starter - Save $3/mo
                      </Button>
                    </Paper>
                  </Grid>
                </Grid>

                {/* Bonus Banner */}
                <Paper
                  sx={{
                    background: 'linear-gradient(135deg, #10B981 0%, #14B8A6 100%)',
                    borderRadius: '16px',
                    p: 3,
                    border: '1px solid #34D399',
                    mb: 3,
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: '0 10px 25px rgba(16, 185, 129, 0.3)',
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: 128,
                      height: 128,
                      bgcolor: 'rgba(255,255,255,0.1)',
                      borderRadius: '50%',
                      mr: -8,
                      mt: -8,
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      left: 0,
                      width: 96,
                      height: 96,
                      bgcolor: 'rgba(255,255,255,0.1)',
                      borderRadius: '50%',
                      ml: -6,
                      mb: -6,
                    }}
                  />
                  <Box sx={{ display: 'flex', alignItems: 'start', gap: 2, position: 'relative', zIndex: 1 }}>
                    <Avatar
                      sx={{
                        width: 48,
                        height: 48,
                        bgcolor: 'white',
                        borderRadius: '12px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      }}
                    >
                      <Gift size={28} color="#10B981" />
                    </Avatar>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography sx={{ fontSize: '18px', fontWeight: 700, color: 'white' }}>
                          🎁 Exclusive Upgrade Bonus
                        </Typography>
                        <Chip
                          label="$500 VALUE"
                          size="small"
                          sx={{
                            bgcolor: '#FCD34D',
                            color: '#78350F',
                            fontWeight: 600,
                            fontSize: '11px',
                          }}
                        />
                      </Box>
                      <Typography sx={{ color: 'white', fontWeight: 500, fontSize: '16px' }}>
                        Upgrade now and get{' '}
                        <Typography component="span" sx={{ fontWeight: 700, textDecoration: 'underline' }}>
                          free setup assistance + 2 hours of consulting
                        </Typography>{' '}
                        at no extra cost!
                      </Typography>
                    </Box>
                  </Box>
                </Paper>

                <Grid container spacing={1.5}>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      onClick={() => setStep(0.5)}
                      variant="outlined"
                      sx={{
                        borderColor: '#E2E8F0',
                        color: '#334155',
                        fontWeight: 500,
                        textTransform: 'none',
                        borderRadius: '8px',
                        py: 1.5,
                        '&:hover': { bgcolor: '#F8FAFC', borderColor: '#CBD5E1' },
                      }}
                    >
                      Go Back
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      onClick={() => setStep(1.6)}
                      sx={{
                        bgcolor: '#F1F5F9',
                        color: '#334155 !important',
                        fontWeight: 500,
                        textTransform: 'none',
                        borderRadius: '8px',
                        py: 1.5,
                        '&:hover': { bgcolor: '#E2E8F0' },
                      }}
                    >
                      Still Not Sure
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* Step 1: Paid - We're sorry to see you go */}
            {/* {step === 1 && cancellingSubscription === 'paid' && (
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#0F172A', mb: 0.5 }}>
                      We're sorry to see you go
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748B' }}>
                      Choose an option below before canceling
                    </Typography>
                  </Box>
                  <IconButton onClick={closeModal} sx={{ color: '#94A3B8' }}>
                    <X size={20} />
                  </IconButton>
                </Box>

                <Button
                  fullWidth
                  onClick={() => setStep(2)}
                  endIcon={<ArrowRight size={16} />}
                  sx={{
                    bgcolor: '#4F46E5',
                    color: 'white',
                    fontWeight: 500,
                    textTransform: 'none',
                    borderRadius: '8px',
                    py: 1.5,
                    fontSize: '14px',
                    '&:hover': {
                      bgcolor: '#4338CA',
                      boxShadow: '0 4px 12px rgba(79, 70, 229, 0.4)',
                    },
                  }}
                >
                  Explore More Options
                </Button>
              </Box>
            )} */}

            {/* Step 1.5: Pause Options */}
            {step === 1.5 && (
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#0F172A', mb: 0.5 }}>
                      Choose pause option
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748B' }}>
                      Just ${pausePlanPrice}/month to keep all your data safe. Cancel pause anytime.
                    </Typography>
                  </Box>
                  <IconButton onClick={closeModal} sx={{ color: '#94A3B8' }}>
                    <X size={20} />
                  </IconButton>
                </Box>

                <Stack spacing={1.5}>
                  <HoverCard
                    onClick={() => !isPausing && handleAcceptOffer('pause-immediate')}
                    sx={{
                      bgcolor: '#FAF5FF',
                      borderRadius: '16px',
                      p: 2.5,
                      border: '1px solid #E9D5FF',
                      cursor: isPausing ? 'not-allowed' : 'pointer',
                      opacity: isPausing ? 0.6 : 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
                      <Box sx={{ display: 'flex', width: 40, height: 40 }}>
                        {isPausing ? (
                          <CircularProgress size={24} sx={{ color: '#7C3AED' }} />
                        ) : (
                          <Zap size={24} color="#7C3AED" />
                        )}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography sx={{ fontWeight: 600, color: '#0F172A' }}>
                            {isPausing ? 'Pausing...' : 'Pause Immediately'}
                          </Typography>
                          {!isPausing && <ArrowRight size={20} color="#94A3B8" />}
                        </Box>
                        <Typography variant="body2" sx={{ color: '#64748B', mb: 1 }}>
                          Start pause now. Your data remains safe and accessible when you return.
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Database size={14} color="#64748B" />
                          <Typography variant="caption" sx={{ color: '#64748B' }}>
                            All data preserved
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </HoverCard>

                  {/* Pause at End of Billing Term - hidden per product decision
                  <HoverCard
                    onClick={() => !isPausing && handleAcceptOffer('pause-end-billing')}
                    sx={{
                      bgcolor: '#EFF6FF',
                      borderRadius: '16px',
                      p: 2.5,
                      border: '1px solid #BFDBFE',
                      cursor: isPausing ? 'not-allowed' : 'pointer',
                      opacity: isPausing ? 0.6 : 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
                      <Box sx={{ display: 'flex', width: 40, height: 40 }}>
                        {isPausing ? (
                          <CircularProgress size={24} sx={{ color: '#3B82F6' }} />
                        ) : (
                          <CreditCard size={24} color="#3B82F6" />
                        )}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Typography sx={{ fontWeight: 600, color: '#0F172A' }}>
                            {isPausing ? 'Pausing...' : 'Pause at End of Billing Term'}
                          </Typography>
                          {!isPausing && <ArrowRight size={20} color="#94A3B8" />}
                        </Box>
                        <Typography variant="body2" sx={{ color: '#64748B', mb: 1 }}>
                          Continue using until your period ends, then pause begins. No data loss.
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Database size={14} color="#64748B" />
                          <Typography variant="caption" sx={{ color: '#64748B' }}>
                            All data preserved
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  </HoverCard>
                  */}

                  <Button
                    fullWidth
                    onClick={() => setStep(0.5)}
                    variant="outlined"
                    sx={{
                      borderColor: '#E2E8F0',
                      color: '#334155',
                      fontWeight: 500,
                      textTransform: 'none',
                      borderRadius: '8px',
                      py: 1.5,
                      '&:hover': { bgcolor: '#F8FAFC', borderColor: '#CBD5E1' },
                    }}
                  >
                    Go Back
                  </Button>
                </Stack>
              </Box>
            )}

            {/* Step 2: Paid - Let's find a better solution */}
            {step === 2 && cancellingSubscription === 'paid' && (
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: '#0F172A', mb: 0.5 }}>
                      Let's find a better solution
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748B' }}>
                      Choose the option that works best for you
                    </Typography>
                  </Box>
                  <IconButton onClick={closeModal} sx={{ color: '#94A3B8' }}>
                    <X size={20} />
                  </IconButton>
                </Box>

                <Stack spacing={1.5}>
                  {/* <HoverCard
                    onClick={() => handleAcceptOffer('30-day-extension')}
                    sx={{
                      bgcolor: '#ECFDF5',
                      borderRadius: '16px',
                      p: 2.5,
                      border: '1px solid #BBF7D0',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
                      <Avatar sx={{ bgcolor: 'white', width: 40, height: 40, borderRadius: '12px' }}>
                        <Gift size={24} color="#10B981" />
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ fontWeight: 600, color: '#0F172A' }}>
                              Extend for 30 days
                            </Typography>
                            <Chip
                              label="Free"
                              size="small"
                              sx={{ bgcolor: '#D1FAE5', color: '#065F46', height: 20, fontSize: '11px' }}
                            />
                          </Box>
                          <ArrowRight size={20} color="#94A3B8" />
                        </Box>
                        <Typography variant="body2" sx={{ color: '#64748B' }}>
                          We'll give you 30 extra days at no charge. No commitment required.
                        </Typography>
                      </Box>
                    </Box>
                  </HoverCard> */}

                  <Grid container spacing={1.5}>
                    <Grid item xs={6} sx={{width: '100%'}}>
                      <HoverCard
                        onClick={() => handleAcceptOffer('free-plan')}
                        sx={{
                          bgcolor: '#FEF3C7',
                          borderRadius: '16px',
                          p: 2.5,
                          paddingLeft: 0,
                          border: '1px solid #FDE68A',
                          height: 140,
                          display: 'flex',
                          flexDirection: 'column', 
                          boxShadow: 'none'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'start', gap: 1.5, mb: 1, paddingLeft: '15px' }}>
                          <Avatar sx={{ bgcolor: 'white', width: 32, height: 32, borderRadius: '10px' }}>
                            <CreditCard size={20} color="#F59E0B" />
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 600, color: '#0F172A', fontSize: '13px', mb: 0.5 }}>
                              Downgrade to Free Plan
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#64748B', display: 'block', lineHeight: 1.4 }}>
                              Keep using basic features with limited access. Upgrade anytime.
                            </Typography>
                          </Box>
                          <ArrowRight size={16} color="#94A3B8" style={{ flexShrink: 0, marginTop: 2 }} />
                        </Box>
                      </HoverCard>
                    </Grid>

                    <Grid item xs={6}>
                      <HoverCard
                        onClick={() => setStep(3)}
                        sx={{
                          bgcolor: '#FEF2F2',
                          borderRadius: '16px',
                          p: 2.5,
                          border: '1px solid #FECACA',
                          height: 140,
                          display: 'flex',
                          flexDirection: 'column',
                          boxShadow: 'none'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'start', gap: 1.5, mb: 1 }}>
                          <Avatar sx={{ bgcolor: 'white', width: 32, height: 32, borderRadius: '10px' }}>
                            <XCircle size={20} color="#EF4444" />
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 600, color: '#0F172A', fontSize: '13px', mb: 0.5 }}>
                              Cancel Subscription
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#64748B', display: 'block', lineHeight: 1.4 }}>
                              End your subscription and lose access to premium features.
                            </Typography>
                          </Box>
                          <ArrowRight size={16} color="#94A3B8" style={{ flexShrink: 0, marginTop: 2 }} />
                        </Box>
                      </HoverCard>
                    </Grid>
                  </Grid>

                  <Button
                    fullWidth
                    onClick={() => setStep(0.5)}
                    variant="outlined"
                    sx={{
                      borderColor: '#E2E8F0',
                      color: '#334155',
                      fontWeight: 500,
                      textTransform: 'none',
                      borderRadius: '8px',
                      py: 1.5,
                      '&:hover': { bgcolor: '#F8FAFC', borderColor: '#CBD5E1' },
                    }}
                  >
                    Go Back
                  </Button>
                </Stack>
              </Box>
            )}

            {/* Step 3: Feedback Form */}
            {step === 3 && (
              <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#0F172A', mb: 0.5 }}>
                      Help us improve
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748B' }}>
                      Your feedback helps us serve you better in the future
                    </Typography>
                  </Box>
                  <IconButton onClick={closeModal} sx={{ color: '#94A3B8' }}>
                    <X size={20} />
                  </IconButton>
                </Box>

                <Stack spacing={2}>
                  <FormControl fullWidth>
                    <InputLabel
                      sx={{
                        fontWeight: 400,
                        color: '#0F172A',
                        '&.Mui-focused': { color: '#6366F1' },
                      }}
                    >
                      What's your main reason for canceling?
                    </InputLabel>
                    <Select
                      value={selectedReason}
                      onChange={(e: SelectChangeEvent) => setSelectedReason(e.target.value)}
                      label="What's your main reason for canceling?"
                      IconComponent={ChevronDown}
                      sx={{
                        borderRadius: '12px',
                        lineHeight: '34px',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#6366F1',
                          borderWidth: '1px',
                        },
                      }}
                    >
                      <MenuItem value="" sx={{ fontWeight: 400 }}>Select a reason</MenuItem>
                      {cancellationReasons.map((reason) => (
                        <MenuItem key={reason.value} value={reason.value} sx={{ fontWeight: 400, lineHeight: '34px' }}>
                          {reason.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    label="Additional feedback (optional)"
                    placeholder="Tell us more about your experience..."
                    value={feedback}
                    onChange={(e) => setFeedback(e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '12px',
                      },
                    }}
                  />

                  {selectedReason && interventionBanners[selectedReason as keyof typeof interventionBanners] && (
                    <Paper
                      sx={{
                        p: 3,
                        borderRadius: '16px',
                        bgcolor: interventionBanners[selectedReason as keyof typeof interventionBanners].bgColor,
                        border: `1px solid ${
                          interventionBanners[selectedReason as keyof typeof interventionBanners].borderColor
                        }`,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'start', gap: 2 }}>
                        <Avatar sx={{ bgcolor: 'white', width: 48, height: 48 }}>
                          <span style={{ fontSize: '24px' }}>
                            {interventionBanners[selectedReason as keyof typeof interventionBanners].icon}
                          </span>
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontWeight: 700, mb: 1 }}>
                            {interventionBanners[selectedReason as keyof typeof interventionBanners].title}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#64748B', mb: 2 }}>
                            {interventionBanners[selectedReason as keyof typeof interventionBanners].description}
                          </Typography>
                          <Button
                            fullWidth
                            onClick={() => handleAcceptOffer(`intervention-${selectedReason}`)}
                            sx={{
                              background:
                                interventionBanners[selectedReason as keyof typeof interventionBanners]
                                  .buttonGradient,
                              color: 'white !important',
                              fontWeight: 600,
                              textTransform: 'none',
                              borderRadius: '12px',
                              py: 1.5,
                              '&:hover': { opacity: 0.9 },
                            }}
                          >
                            {interventionBanners[selectedReason as keyof typeof interventionBanners].buttonText}
                          </Button>
                        </Box>
                      </Box>
                    </Paper>
                  )}

                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Button
                        fullWidth
                        onClick={() => setStep(cancellingSubscription === 'trial' ? 1.6 : 2)}
                        variant="outlined"
                        sx={{
                          borderColor: '#E2E8F0',
                          color: '#64748B',
                          fontWeight: 600,
                          textTransform: 'none',
                          borderRadius: '8px',
                          py: 1.5,
                        }}
                      >
                        Go Back
                      </Button>
                    </Grid>
                    <Grid item xs={6}>
                      <Button
                        fullWidth
                        onClick={() => handleAcceptOffer('final-cancel')}
                        disabled={!selectedReason}
                        sx={{
                          bgcolor: '#FEF2F2 !important',
                          color: '#dc2626 !important',
                          border: '1px solid #FECACA !important',
                          fontWeight: 600,
                          textTransform: 'none',
                          borderRadius: '8px',
                          py: 1.5,
                          '&:hover': { bgcolor: '#FEE2E2 !important',
                            border: '1px solid #dc2626 !important',
                            outline: 'none',
                            boxShadow: 'none',
                           },
                          '&:disabled': {
                            bgcolor: '#F5F5F5 !important',
                            color: '#9CA3AF !important',
                            border: '1px solid #E5E7EB !important',
                          },
                        }}
                      >
                        Cancel Subscription
                      </Button>
                    </Grid>
                  </Grid>
                </Stack>
              </Box>
            )}
          </AnimatedBox>
        </Box>
      </StyledDialog>

      {/* Success Dialog */}
      <StyledDialog open={showSuccess} onClose={closeSuccessModal}>
        <Box sx={{ p: 6, textAlign: 'center' }}>
          <AnimatedBox>
            {successMessage.includes('cancelled') ? (
              <>
                <Box sx={{ position: 'relative', display: 'inline-block', mb: 3 }}>
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      bgcolor: '#94A3B8',
                      borderRadius: '50%',
                      filter: 'blur(40px)',
                      opacity: 0.3,
                    }}
                  />
                  <Avatar
                    sx={{
                      position: 'relative',
                      width: 96,
                      height: 96,
                      background: 'linear-gradient(135deg, #94A3B8 0%, #64748B 100%)',
                    }}
                  >
                    <XCircle size={56} />
                  </Avatar>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#0F172A', mb: 2 }}>
                  We're sorry to see you go
                </Typography>
                <Typography variant="body1" sx={{ color: '#64748B', mb: 2 }}>
                  {successMessage}
                </Typography>
                <Paper sx={{ bgcolor: '#FEF3C7', border: '1px solid #FDE68A', p: 2, borderRadius: '12px', mb: 3 }}>
                  <Typography variant="body2" sx={{ color: '#92400E', fontWeight: 500 }}>
                    💡 You're missing out on exclusive features and benefits. We're here if you change your mind!
                  </Typography>
                </Paper>
                <Button
                  onClick={() => {
                    closeSuccessModal();
                    navigate?.('/subscription');
                  }}
                  sx={{
                    px: 4,
                    py: 1.5,
                    bgcolor: '#64748B',
                    color: 'white !important',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '12px',
                    '&:hover': { bgcolor: '#475569' },
                  }}
                >
                  Close
                </Button>
              </>
            ) : successMessage.includes('paused') || successMessage.includes('pause') ? (
              <>
                <Box sx={{ position: 'relative', display: 'inline-block', mb: 3 }}>
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      bgcolor: '#60A5FA',
                      borderRadius: '50%',
                      filter: 'blur(40px)',
                      opacity: 0.3,
                    }}
                  />
                  <Avatar
                    sx={{
                      position: 'relative',
                      width: 96,
                      height: 96,
                      background: 'linear-gradient(135deg, #3B82F6 0%, #6366F1 100%)',
                    }}
                  >
                    <Pause size={56} />
                  </Avatar>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#0F172A', mb: 2 }}>
                  Account Paused
                </Typography>
                <Typography variant="body1" sx={{ color: '#64748B', mb: 2 }}>
                  {successMessage}
                </Typography>
                <Paper
                  sx={{ bgcolor: '#EFF6FF', border: '1px solid #BFDBFE', p: 2, borderRadius: '12px', mb: 2 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'start', gap: 1.5, textAlign: 'left' }}>
                    <Database size={20} color="#3B82F6" style={{ marginTop: 2, flexShrink: 0 }} />
                    <Box>
                      <Typography sx={{ fontWeight: 600, color: '#1E3A8A', fontSize: '14px', mb: 0.5 }}>
                        Your data is safe
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#1E40AF', display: 'block' }}>
                        All your projects, funnels, and data remain securely stored. Resume anytime to pick up
                        exactly where you left off.
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
                <Paper
                  sx={{ bgcolor: '#FAF5FF', border: '1px solid #E9D5FF', p: 2, borderRadius: '12px', mb: 3 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'start', gap: 1.5, textAlign: 'left' }}>
                    <CreditCard size={20} color="#7C3AED" style={{ marginTop: 2, flexShrink: 0 }} />
                    <Box>
                      <Typography sx={{ fontWeight: 600, color: '#581C87', fontSize: '14px', mb: 0.5 }}>
                        Billing: ${pausePlanPrice}/month
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#6B21A8', display: 'block' }}>
                        You'll be charged ${pausePlanPrice}/month to keep your data preserved. Cancel pause anytime
                        in your settings.
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
                <Button
                  onClick={() => {
                    closeSuccessModal();
                    navigate?.('/subscription');
                  }}
                  sx={{
                    px: 4,
                    py: 1.5,
                    bgcolor: '#3B82F6',
                    color: 'white !important',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '12px',
                    '&:hover': { bgcolor: '#2563EB' },
                  }}
                >
                  Got it
                </Button>
              </>
            ) : (
              <>
                <Box sx={{ position: 'relative', display: 'inline-block', mb: 3 }}>
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      bgcolor: '#10B981',
                      borderRadius: '50%',
                      filter: 'blur(40px)',
                      opacity: 0.5,
                      animation: `${keyframes`0%, 100% { opacity: 0.5; } 50% { opacity: 0.3; }`} 2s ease-in-out infinite`,
                    }}
                  />
                  <BounceBox>
                    <Avatar
                      sx={{
                        position: 'relative',
                        width: 96,
                        height: 96,
                        background: 'linear-gradient(135deg, #10B981 0%, #14B8A6 100%)',
                      }}
                    >
                      <CheckCircle2 size={56} />
                    </Avatar>
                  </BounceBox>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#0F172A', mb: 2 }}>
                  Perfect choice!
                </Typography>
                <Typography variant="body1" sx={{ color: '#64748B', mb: 3 }}>
                  {successMessage}
                </Typography>
                <Button
                  onClick={closeSuccessModal}
                  sx={{
                    px: 4,
                    py: 1.5,
                    bgcolor: '#4F46E5',
                    color: 'white !important',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: '12px',
                    '&:hover': { bgcolor: '#4338CA' },
                  }}
                >
                  Close
                </Button>
              </>
            )}
          </AnimatedBox>
        </Box>
      </StyledDialog>
    </>
  );
};

export default CancelSubscriptionModal;